// Kopie z FofrLamaPOS (components/pos/StulDlazdice.jsx) — editor stolů je v pokladně i ve Správě OS
// (Správa → Provozovna → Stoly, 2026-10-20). Měň obojí stejně.
import { Plus, X, CalendarClock } from 'lucide-react'
import { COLORS, hexToRgba } from './sdilene'
import { kc, cas, stavStoluConfig, barvaUklidu } from './sdilene'
import { ZIDLE, KULATE, STRANY, zidleStolu, rozmistit } from './zidle'

/**
 * Stoly mají rovné hrany. Kruh je kruh, ovál je obdélník s půlkruhovými
 * kratšími stranami (jako skutečný oválný stůl), ne elipsa.
 */
const zaobleni = (tvar, sirkaPx, vyskaPx) =>
  tvar === 'kruh' ? '50%' : tvar === 'oval' ? Math.min(sirkaPx, vyskaPx) / 2 : 3

/**
 * Dlaždice stolu na půdorysu. Pozice a rozměr přicházejí v px plátna,
 * měřítko aplikuje rodič — proto tady žádný přepočet, jen hotové hodnoty.
 *
 * Stav se pozná barvou i ikonou; na samotnou barvu se nespoléháme.
 */
export default function StulDlazdice({
  stul,
  // Dvě měřítka: plátno se roztahuje na celou plochu, takže osy mají
  // vlastní.  je to menší z nich — text se s plátnem zvětšuje
  // a zmenšuje, ale nedeformuje.
  meritkoX,
  meritkoY,
  meritkoPisma,
  editace = false,
  vybrany = false,
  onClick,
  onSmazat,
  onZacitTah,
  onZacitZmenuVelikosti,
  onPridatZidli,
  onOdebratZidli
}) {
  const { x, y, sirka, vyska, tvar } = stul.pozice
  const stav = stavStoluConfig(stul.stav)
  const Ikona = stav.icon
  const ucet = stul.ucet
  // Poloha se roztahuje s plátnem (plán vyplní obrazovku), ale tvar ne:
  // stůl se kreslí jedním měřítkem v obou osách, kolem svého středu. Jinak
  // by se na širokém displeji kruh roztáhl v ovál a čtverec v obdélník.
  const sirkaPx = sirka * meritkoPisma
  const vyskaPx = vyska * meritkoPisma
  const levo = (x + sirka / 2) * meritkoX - sirkaPx / 2
  const nahore = (y + vyska / 2) * meritkoY - vyskaPx / 2
  // Písmo v jednotkách plátna, ne v pevných pixelech — jinak by se na malé
  // obrazovce nevešlo do dlaždice a na velké se v ní ztratilo.
  const px = (jednotky) => Math.max(8, Math.round(jednotky * meritkoPisma))

  // Dekorace (bar, pult, sloup) — jen popisek, nedá se kliknout ani otevřít účet.
  if (stul.dekorace) {
    return (
      <div
        className="absolute flex items-center justify-center text-center font-semibold uppercase"
        style={{
          left: levo,
          top: nahore,
          width: sirkaPx,
          height: vyskaPx,
          borderRadius: zaobleni(tvar, sirkaPx, vyskaPx),
          fontSize: px(13),
          // Prvek pro orientaci (bar, vchod, okno) — neutrální, ať se neplete
          // se stoly a jejich stavy.
          background: 'rgba(var(--tint-rgb),0.07)',
          border: `1px solid ${vybrany ? COLORS.brand : COLORS.borderStrong}`,
          color: COLORS.textSecondary,
          letterSpacing: 0.8,
          padding: 4,
          lineHeight: 1.15,
          overflow: 'hidden',
          cursor: editace ? 'grab' : 'default',
          touchAction: 'none'
        }}
        onPointerDown={editace ? (e) => onZacitTah?.(e, stul) : undefined}
      >
        {stul.nazev}
        {editace && <ZmenaVelikosti onPointerDown={(e) => onZacitZmenuVelikosti?.(e, stul)} />}
      </div>
    )
  }

  const obsazeny = stul.stav !== 'volny'
  const kUklidu = stul.stav === 'k_uklidu'
  // Text i rámeček drží barvu stavu; volný stůl zůstává tlumený, ať na
  // půdorysu vystoupí to, co si žádá pozornost. Stůl, který čeká na úklid
  // dlouho, se navíc překlopí do varovné barvy — úkol sám nikdy nezhasne,
  // takže tlak musí dělat viditelnost.
  const barva = kUklidu ? barvaUklidu(stul.uklid) : stav.color

  return (
    <button
      onClick={editace ? undefined : onClick}
      onPointerDown={editace ? (e) => onZacitTah?.(e, stul) : undefined}
      // Stůl, na kterém se něco děje (účet, úklid, rezervace), je papír —
      // je z něj co číst. Volný zůstává tmavý a ustoupí do pozadí.
      className="sklo absolute flex flex-col items-center justify-center gap-0.5 transition-transform"
      style={{
        left: levo,
        top: nahore,
        width: sirkaPx,
        height: vyskaPx,
        borderRadius: zaobleni(tvar, sirkaPx, vyskaPx),
        // Sklo jako dlaždice nabídky. Obsazený stůl se podbarví barvou stavu
        // a dostane výrazný okraj — z dálky musí být vidět, kde se sedí.
        ...(obsazeny
          ? {
              background: `linear-gradient(150deg, ${hexToRgba(barva, 0.42)} 0%, ${hexToRgba(barva, 0.16)} 100%)`,
              border: `2px solid ${barva}`
            }
          : {}),
        color: obsazeny ? COLORS.textPrimary : COLORS.textSecondary,
        boxShadow: vybrany ? `0 0 0 2px ${hexToRgba(COLORS.brand, 0.6)}` : 'none',
        cursor: editace ? 'grab' : 'pointer',
        touchAction: 'none'
      }}
    >
      <div className="flex items-center gap-1">
        {Ikona && <Ikona size={px(12)} style={{ color: barva }} />}
        <span className="font-bold" style={{ fontSize: px(20), letterSpacing: -0.4, lineHeight: 1.1 }}>
          {stul.cislo}
        </span>
      </div>

      {obsazeny && ucet ? (
        <>
          <span className="font-bold tnum" style={{ fontSize: px(14), color: barva }}>
            {kc(ucet.celkem)}
          </span>
          <span className="tnum" style={{ fontSize: px(12), color: COLORS.textMuted }}>
            {ucet.pocet_hostu ? `${ucet.pocet_hostu} hostů · ` : ''}
            {cas(ucet.zalozeno_v)}
          </span>
        </>
      ) : kUklidu && !editace ? (
        // Zaplaceno, ale neutřeno. Odpočet je tu schválně: „8 min" řekne
        // obsluze víc než samotná barva a je to jediný tlak, který na úkol
        // působí — sám od sebe nezhasne.
        <>
          <span className="font-bold tnum" style={{ fontSize: px(14), color: barva }}>
            {stul.uklid ? `${stul.uklid.minut} min` : 'k úklidu'}
          </span>
          <span
            className="truncate"
            style={{ fontSize: px(12), color: COLORS.textMuted, maxWidth: '92%' }}
          >
            {/* Proč zrovna tenhle stůl spěchá — hosté už jsou na cestě. */}
            {stul.rezervace ? `rezervace ${cas(stul.rezervace.zacatek)}` : 'utřít'}
          </span>
        </>
      ) : stul.rezervace && !editace ? (
        // Rezervovaný stůl je obsazený, ale účet na něm ještě není. Bez téhle
        // větve by na dlaždici svítilo „volný" — přesně to, co obsluhu zmate.
        <>
          <span className="font-bold tnum" style={{ fontSize: px(14), color: barva }}>
            {cas(stul.rezervace.zacatek)}
          </span>
          <span
            className="truncate"
            style={{ fontSize: px(12), color: COLORS.textMuted, maxWidth: '92%' }}
          >
            {stul.rezervace.jmeno} · {stul.rezervace.pocet_hostu}
          </span>
        </>
      ) : (
        !editace && (
          <span
            className="flex items-center gap-1"
            style={{ fontSize: px(12), color: COLORS.textMuted }}
          >
            <Plus size={px(12)} />
            volný
          </span>
        )
      )}

      {/* Rezervace na stole, podle toho, kdy přijde:
          - dnes, ale za víc než předstih (`stul.rezervace` ještě není) → tečka;
          - v předstihu na volném stole → celý stůl modrý (stav „Rezervace“);
          - v předstihu na obsazeném stole → bublina s časem a hosty, ať
            obsluha ví, že stůl musí uvolnit. */}
      {!editace && stul.rezervace && stul.ucet && (
        <span
          className="absolute flex items-center gap-1 tnum font-bold"
          title={`Rezervace ${cas(stul.rezervace.zacatek)} · ${stul.rezervace.pocet_hostu} os. · ${stul.rezervace.jmeno ?? ''}`}
          style={{
            top: -px(11),
            left: '50%',
            transform: 'translateX(-50%)',
            padding: `${px(2)}px ${px(7)}px`,
            borderRadius: 999,
            fontSize: px(12),
            background: 'var(--info)',
            color: '#fff',
            boxShadow: '0 2px 6px rgba(0,0,0,0.45)',
            whiteSpace: 'nowrap',
            zIndex: 2
          }}
        >
          <CalendarClock size={px(12)} />
          {cas(stul.rezervace.zacatek)} · {stul.rezervace.pocet_hostu}
        </span>
      )}
      {!editace && !stul.rezervace && stul.rezervace_dnes?.length > 0 && (
        <span
          className="absolute"
          title={stul.rezervace_dnes.map((r) => `Dnes ${cas(r.zacatek)} · ${r.pocet_hostu} os.`).join('\n')}
          style={{
            top: px(7),
            right: px(7),
            width: px(10),
            height: px(10),
            borderRadius: '50%',
            background: 'var(--info)',
            boxShadow: '0 0 0 2px rgba(0,0,0,0.35)'
          }}
        />
      )}

      {/* Počet míst = židle kolem stolu. V editoru se ťuknutím na židli
          vybraného stolu odebírá. */}
      <Zidle
        stul={stul}
        meritko={meritkoPisma}
        barva={obsazeny ? barva : null}
        onOdebrat={editace && vybrany ? onOdebratZidli : null}
      />
      {editace && vybrany && onPridatZidli && (
        <PridatZidle stul={stul} meritko={meritkoPisma} onPridat={onPridatZidli} />
      )}

      {editace && (
        <>
          <span
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              onSmazat?.(stul)
            }}
            title="Odebrat stůl z půdorysu"
            className="absolute flex items-center justify-center"
            style={{
              top: -8,
              right: -8,
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: COLORS.danger,
              color: '#fff',
              cursor: 'pointer'
            }}
          >
            <X size={12} />
          </span>
          <ZmenaVelikosti onPointerDown={(e) => onZacitZmenuVelikosti?.(e, stul)} />
        </>
      )}
    </button>
  )
}

/** Židle kolem stolu — rozmístěné rovnoměrně, přichycené k hraně. */
function Zidle({ stul, meritko, barva, onOdebrat }) {
  const { sirka, vyska, tvar } = stul.pozice
  const mista = rozmistit(sirka, vyska, tvar, zidleStolu(stul))
  const w = ZIDLE.sirka * meritko
  const h = ZIDLE.hloubka * meritko
  return mista.map((m, i) => (
    <span
      key={i}
      onPointerDown={onOdebrat ? (e) => e.stopPropagation() : undefined}
      onClick={
        onOdebrat
          ? (e) => {
              e.stopPropagation()
              onOdebrat(m.strana)
            }
          : undefined
      }
      title={onOdebrat ? 'Odebrat židli' : undefined}
      className="absolute"
      style={{
        left: m.cx * meritko - w / 2,
        top: m.cy * meritko - h / 2,
        width: w,
        height: h,
        transform: `rotate(${m.uhel}deg)`,
        // Opěradlo je na vnější straně — silnější horní hrana.
        borderRadius: `${Math.max(2, h * 0.45)}px ${Math.max(2, h * 0.45)}px 2px 2px`,
        background: barva ? hexToRgba(barva, 0.45) : 'rgba(var(--tint-rgb),0.16)',
        border: `1px solid ${barva ? hexToRgba(barva, 0.8) : 'rgba(var(--tint-rgb),0.28)'}`,
        cursor: onOdebrat ? 'pointer' : 'inherit',
        pointerEvents: onOdebrat ? 'auto' : 'none'
      }}
    />
  ))
}

/** „+" u každé strany vybraného stolu (u kruhu a oválu jedno). */
function PridatZidle({ stul, meritko, onPridat }) {
  const { sirka, vyska, tvar } = stul.pozice
  const daleko = ZIDLE.mezera + ZIDLE.hloubka + 16
  const body = KULATE.includes(tvar)
    ? [{ strana: 'kolem', cx: sirka + daleko * 0.7, cy: -daleko * 0.7 }]
    : STRANY.map((strana) => ({
        strana,
        cx: strana === 'vlevo' ? -daleko : strana === 'vpravo' ? sirka + daleko : sirka / 2,
        cy: strana === 'nahore' ? -daleko : strana === 'dole' ? vyska + daleko : vyska / 2
      }))
  const r = Math.max(22, 26 * meritko)
  return body.map((b) => (
    <span
      key={b.strana}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation()
        onPridat(b.strana)
      }}
      title="Přidat židli"
      className="absolute flex items-center justify-center"
      style={{
        left: b.cx * meritko - r / 2,
        top: b.cy * meritko - r / 2,
        width: r,
        height: r,
        borderRadius: '50%',
        background: COLORS.brand,
        color: '#fff',
        boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
        cursor: 'pointer',
        zIndex: 2
      }}
    >
      <Plus size={Math.round(r * 0.6)} />
    </span>
  ))
}

/** Úchyt pro změnu velikosti v pravém dolním rohu. */
function ZmenaVelikosti({ onPointerDown }) {
  return (
    <span
      onPointerDown={(e) => {
        e.stopPropagation()
        onPointerDown?.(e)
      }}
      title="Změnit velikost"
      className="absolute"
      style={{
        right: -4,
        bottom: -4,
        width: 16,
        height: 16,
        borderRadius: 4,
        background: COLORS.brand,
        border: '2px solid rgba(0,0,0,0.35)',
        cursor: 'nwse-resize',
        touchAction: 'none'
      }}
    />
  )
}
